/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';

interface TokenizeAssessment {
  _id: string;
  // Step 1 — objectives
  objectives?: string[];
  painPoint?: string;
  // Step 2 — institution profile
  institutionType?: string;
  assetTypes?: string[];
  assetTypesOther?: string;
  aum?: string;
  tokenisationPipeline?: string;
  investorBase?: string;
  firstDeal?: string;
  firstDealDetails?: string;
  // Step 3 — jurisdiction
  jurisdiction?: string;
  jurisdictionOther?: string;
  investorLocations?: string[];
  regulatoryStatus?: string;
  supportNeeded?: string[];
  // Step 4 — contact
  contactName?: string;
  jobTitle?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  website?: string;
  referralSource?: string;
  timeline?: string;
  nextStep?: string;
  additionalInfo?: string;
  consent?: boolean;
  submittedAt?: string;
}

const PANEL_STYLE: React.CSSProperties = {
  backgroundColor: '#0B222C',
  border: '2px solid #095D52',
  boxShadow:
    '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
  backgroundImage:
    'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
};

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('en-GB') : '—');

const formatList = (value?: string[]) => (value && value.length ? value.join(', ') : '—');

/** One step's worth of answers in the detail modal. */
const StepBlock = ({ title, rows }: { title: string; rows: Array<[string, string]> }) => (
  <div className='mb-6'>
    <h3
      className='text-xs uppercase tracking-wider mb-3 pb-2'
      style={{ color: '#bbe425', borderBottom: '1px solid rgba(187, 228, 37, 0.2)' }}
    >
      {title}
    </h3>
    <div className='space-y-3'>
      {rows.map(([label, value]) => (
        <div key={label} className='grid grid-cols-3 gap-3 items-start'>
          <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>
            {label}
          </div>
          <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>
            {value || '—'}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Partnership assessments submitted through the 4-step /tokenize questionnaire. */
const PartnershipAssessmentsPanel: React.FC = () => {
  const [assessments, setAssessments] = useState<TokenizeAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<TokenizeAssessment | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await services.get(
          `${API_ENDPOINTS.TOKENIZE_ASSESSMENTS.LIST}?page=${currentPage}&limit=${itemsPerPage}`
        );

        if (response.data && response.data.success) {
          const data = response.data.data || [];
          setAssessments(Array.isArray(data) ? data : []);
          setTotal(response.data.total || data.length);
          setTotalPages(response.data.pages || 1);
        } else {
          setAssessments([]);
          setTotal(0);
          setTotalPages(1);
        }
      } catch (err: any) {
        console.error('Error fetching partnership assessments:', err);
        setError(err.response?.data?.message || 'Failed to fetch partnership assessments');
        setAssessments([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [currentPage]);

  const search = searchTerm.trim().toLowerCase();
  const visible = search
    ? assessments.filter((a) =>
        [a.contactName, a.email, a.companyName, a.jobTitle]
          .filter(Boolean)
          .some((field) => (field as string).toLowerCase().includes(search))
      )
    : assessments;

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-white text-lg'>Loading partnership assessments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-red-400 text-lg'>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className='flex flex-col md:flex-row md:items-center md:justify-between mb-8'>
        <div>
          <h1
            className='text-3xl font-bold mb-2 font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
              letterSpacing: '0.5px',
            }}
          >
            Partnership Assessments
          </h1>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Submissions from the 4-step tokenisation questionnaire
          </p>
        </div>
        <div className='font-work-sans text-sm' style={{ color: '#ede8c5' }}>
          {total} assessment{total === 1 ? '' : 's'}
        </div>
      </div>

      {/* Search */}
      <div className='mb-8'>
        <div className='rounded-lg p-6 shadow-lg transition-all duration-300' style={PANEL_STYLE}>
          <label
            htmlFor='assessment-search'
            className='block text-sm font-medium mb-2 font-work-sans'
            style={{ color: '#ede8c5' }}
          >
            Search Assessments
          </label>
          <input
            type='text'
            id='assessment-search'
            placeholder='Search by name, email, company, or role...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:border-transparent font-work-sans'
            style={{
              backgroundColor: '#0B222C',
              border: '1px solid #095D52',
              color: '#ede8c5',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className='rounded-lg border shadow-lg transition-all duration-300' style={PANEL_STYLE}>
        {visible.length === 0 ? (
          <div className='p-12 text-center font-work-sans' style={{ color: '#ede8c5' }}>
            <p className='text-lg mb-1'>No assessments found</p>
            <p className='text-sm opacity-75'>
              Assessments will appear here once the questionnaire is submitted
            </p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr style={{ borderBottom: '1px solid #095D52' }}>
                  {['Contact', 'Company', 'Institution', 'Jurisdiction', 'Timeline', 'Submitted', ''].map(
                    (heading) => (
                      <th
                        key={heading}
                        className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider font-work-sans'
                        style={{ color: '#bbe425' }}
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((assessment) => (
                  <tr
                    key={assessment._id}
                    style={{ borderBottom: '1px solid rgba(9, 93, 82, 0.5)' }}
                  >
                    <td className='px-6 py-4'>
                      <div
                        className='text-sm font-semibold font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        {assessment.contactName || '—'}
                      </div>
                      <div
                        className='text-xs font-work-sans opacity-75'
                        style={{ color: '#ede8c5' }}
                      >
                        {assessment.email}
                      </div>
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {assessment.companyName || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {assessment.institutionType || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {assessment.jurisdiction || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {assessment.timeline || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {formatDate(assessment.submittedAt)}
                    </td>
                    <td className='px-6 py-4'>
                      <button
                        onClick={() => setSelected(assessment)}
                        className='px-3 py-1.5 rounded-lg text-xs font-work-sans transition-all duration-200 hover:scale-105'
                        style={{
                          backgroundColor: '#095D52',
                          color: '#ede8c5',
                          border: '1px solid #095D52',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-6'>
          <span className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
            Page {currentPage} of {totalPages}
          </span>
          <div className='flex gap-2'>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className='px-4 py-2 rounded-lg font-work-sans disabled:opacity-40'
              style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
            >
              Prev
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className='px-4 py-2 rounded-lg font-work-sans disabled:opacity-40'
              style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail modal — all four steps */}
      {selected && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center p-4'
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className='rounded-lg p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto'
            style={PANEL_STYLE}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-start justify-between mb-6'>
              <div>
                <h2 className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                  {selected.contactName}
                </h2>
                <p className='text-sm font-work-sans opacity-75' style={{ color: '#ede8c5' }}>
                  {selected.companyName}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className='text-2xl leading-none'
                style={{ color: '#ede8c5' }}
                aria-label='Close'
              >
                ×
              </button>
            </div>

            <div className='font-work-sans'>
              <StepBlock
                title="Step 1 — What's Driving Your Interest"
                rows={[
                  ['Objectives', formatList(selected.objectives)],
                  ['Pain point', selected.painPoint || '—'],
                ]}
              />
              <StepBlock
                title='Step 2 — Institution Profile'
                rows={[
                  ['Institution type', selected.institutionType || '—'],
                  ['Asset types', formatList(selected.assetTypes)],
                  ['Asset types (other)', selected.assetTypesOther || '—'],
                  ['AUM', selected.aum || '—'],
                  ['Tokenisation pipeline', selected.tokenisationPipeline || '—'],
                  ['Investor base', selected.investorBase || '—'],
                  ['First deal readiness', selected.firstDeal || '—'],
                  ['First deal details', selected.firstDealDetails || '—'],
                ]}
              />
              <StepBlock
                title='Step 3 — Jurisdiction & Regulatory'
                rows={[
                  ['Jurisdiction', selected.jurisdiction || '—'],
                  ['Jurisdiction (other)', selected.jurisdictionOther || '—'],
                  ['Investor locations', formatList(selected.investorLocations)],
                  ['Regulatory status', selected.regulatoryStatus || '—'],
                  ['Support needed', formatList(selected.supportNeeded)],
                ]}
              />
              <StepBlock
                title='Step 4 — Contact & Next Steps'
                rows={[
                  ['Name', selected.contactName || '—'],
                  ['Role', selected.jobTitle || '—'],
                  ['Company', selected.companyName || '—'],
                  ['Email', selected.email || '—'],
                  ['Phone', selected.phone || '—'],
                  ['Website', selected.website || '—'],
                  ['Referral source', selected.referralSource || '—'],
                  ['Timeline', selected.timeline || '—'],
                  ['Preferred next step', selected.nextStep || '—'],
                  ['Additional info', selected.additionalInfo || '—'],
                  ['Consent', selected.consent ? 'Given' : 'Not given'],
                  ['Submitted', formatDate(selected.submittedAt)],
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnershipAssessmentsPanel;
